import { 
  Megaphone, 
  Briefcase, 
  GraduationCap, 
  Rocket, 
  Building2, 
  Palette,
  LucideIcon
} from 'lucide-react';
import { useInViewAnimation } from '@/hooks/useInViewAnimation';

interface ProfileItem {
  icon: LucideIcon;
  title: string;
  description: string;
}

interface WhoItsForSectionProps {
  title?: string;
  subtitle?: string;
  profiles?: ProfileItem[];
  bottomText?: string;
  tagline?: string;
}

// Default profiles for landing page
const defaultProfiles: ProfileItem[] = [
  {
    icon: Megaphone,
    title: 'Content Creators & Marketers',
    description: 'Transform campaigns and content ideas into compelling videos that capture attention and drive results.'
  },
  {
    icon: Briefcase,
    title: 'Business Professionals',
    description: 'Create winning presentations, pitches, and comms with professional visual storytelling that delivers your message.'
  },
  {
    icon: GraduationCap,
    title: 'Educators & Trainers',
    description: 'Turn learning materials into captivating videos that boost comprehension and retention.'
  },
  {
    icon: Rocket,
    title: 'Entrepreneurs & Founders',
    description: 'Develop product demos, investor pitches, and company stories that resonate and inspire action.'
  },
  {
    icon: Building2,
    title: 'Teams & Organizations',
    description: 'Scale video creation across departments with consistent, on-brand messaging and professional results.'
  },
  {
    icon: Palette,
    title: 'Creative Professionals',
    description: 'Bring artistic visions to life with structured prompting that delivers cinematic storytelling every time.'
  }
];

function ProfileCard({ profile, index }: { profile: ProfileItem; index: number }) {
  return (
    <div
      className="group flex flex-col items-center space-y-4 text-center p-6 sm:p-8 rounded-2xl bg-card border border-transparent hover:border-primary/20 hover:bg-primary/5 transition-all duration-300 hover:shadow-lg hover:shadow-primary/5 hover:-translate-y-1"
      style={{ animationDelay: `${index * 80}ms` }}
    >
      <div className="bg-primary/10 text-primary rounded-2xl p-4 transition-all duration-300 group-hover:bg-primary/15 group-hover:scale-110">
        <profile.icon className="w-7 h-7 sm:w-8 sm:h-8" />
      </div>
      <h3 className="text-lg sm:text-xl font-semibold text-foreground leading-tight">
        {profile.title}
      </h3>
      <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
        {profile.description}
      </p>
    </div>
  );
}

export function WhoItsForSection({
  title = "Perfect for every creator",
  subtitle = "Built for anyone with a story to tell.",
  profiles = defaultProfiles,
  bottomText = "The AI Prompt Assistant empowers anyone to become a master storyteller.",
  tagline = "Make Anything Interesting™"
}: WhoItsForSectionProps) {
  const { ref, isInView } = useInViewAnimation({ threshold: 0.1 });

  return (
    <section ref={ref} className="py-20 sm:py-28 bg-background">
      <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div 
          className={`text-center mb-14 sm:mb-18 opacity-0 ${isInView ? 'animate-fade-in-up' : ''}`}
        >
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground mb-5 leading-tight">
            {title}
          </h2>
          <p className="text-lg sm:text-xl text-muted-foreground max-w-3xl mx-auto leading-relaxed">
            {subtitle}
          </p>
        </div>

        {/* Profile Cards Grid */}
        <div 
          className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8 opacity-0 ${isInView ? 'animate-fade-in-up' : ''}`}
          style={{ animationDelay: '150ms' }}
        >
          {profiles.map((profile, index) => (
            <ProfileCard key={index} profile={profile} index={index} />
          ))}
        </div>

        {/* Bottom Text */}
        <div 
          className={`mt-16 sm:mt-20 text-center space-y-4 opacity-0 ${isInView ? 'animate-fade-in-up' : ''}`}
          style={{ animationDelay: '300ms' }}
        >
          <p className="text-base sm:text-lg text-muted-foreground max-w-3xl mx-auto leading-relaxed">
            {bottomText}
          </p>
          <p className="text-xl sm:text-2xl font-bold text-primary">
            {tagline}
          </p>
        </div>
      </div>
    </section>
  );
}
